import React from 'react';
import { cn } from '../../utils/cn.ts';
import { ComboboxOptionRow } from './ComboboxOptionRow.tsx';
import type { ComboboxOption, ComboboxRenderState } from './Combobox.types.ts';
import { GlassRegular } from './GlassSurface/index.ts';
import { Input } from './Input.tsx';

interface ComboboxPopoverProps<TValue extends string> {
    activeOptionId: string | undefined;
    activeOptionIndex: number;
    align: 'start' | 'end';
    controlName: string;
    emptyText: string;
    filteredOptions: ComboboxOption<TValue>[];
    fullWidth: boolean;
    initialListScrollTop?: number;
    listClassName?: string;
    listboxId: string;
    maxListHeight: string;
    optionClassName?: string;
    placementStyle?: React.CSSProperties;
    popoverClassName?: string;
    popoverRef?: React.Ref<HTMLDivElement>;
    query: string;
    renderOption?: (
        option: ComboboxOption<TValue>,
        state: ComboboxRenderState
    ) => React.ReactNode;
    searchPlaceholder: string;
    searchInputRef: React.Ref<HTMLInputElement>;
    selectedValue?: TValue | null;
    onActiveIndexChange: (index: number) => void;
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
    onListScrollTopChange?: (scrollTop: number) => void;
    onQueryChange: (query: string) => void;
    onSelect: (option: ComboboxOption<TValue>) => void;
    preserveListScroll?: boolean;
    skipInitialActiveScroll?: boolean;
}

export function ComboboxPopover<TValue extends string>({
    activeOptionId,
    activeOptionIndex,
    controlName,
    emptyText,
    filteredOptions,
    initialListScrollTop,
    listClassName,
    listboxId,
    maxListHeight,
    optionClassName,
    placementStyle,
    popoverClassName,
    popoverRef,
    query,
    renderOption,
    searchPlaceholder,
    searchInputRef,
    selectedValue,
    onActiveIndexChange,
    onKeyDown,
    onListScrollTopChange,
    onQueryChange,
    onSelect,
    preserveListScroll,
    skipInitialActiveScroll,
}: ComboboxPopoverProps<TValue>) {
    return (
        <div
            ref={popoverRef}
            data-combobox-popover=""
            className={cn(
                'fixed z-50',
                popoverClassName
            )}
            style={placementStyle}
        >
            <GlassRegular
                className="flex w-full flex-col gap-2 overflow-hidden p-2"
                data-combobox-popover-surface="regular"
                onKeyDown={onKeyDown}
            >
                <ComboboxSearch
                    activeOptionId={activeOptionId}
                    controlName={controlName}
                    listboxId={listboxId}
                    query={query}
                    searchInputRef={searchInputRef}
                    searchPlaceholder={searchPlaceholder}
                    onQueryChange={onQueryChange}
                />
                <ComboboxListbox
                    activeOptionIndex={activeOptionIndex}
                    controlName={controlName}
                    emptyText={emptyText}
                    filteredOptions={filteredOptions}
                    initialListScrollTop={initialListScrollTop}
                    listClassName={listClassName}
                    listboxId={listboxId}
                    maxListHeight={maxListHeight}
                    optionClassName={optionClassName}
                    query={query}
                    renderOption={renderOption}
                    selectedValue={selectedValue}
                    onActiveIndexChange={onActiveIndexChange}
                    onListScrollTopChange={onListScrollTopChange}
                    onSelect={onSelect}
                    preserveListScroll={preserveListScroll}
                    skipInitialActiveScroll={skipInitialActiveScroll}
                />
            </GlassRegular>
        </div>
    );
}

function ComboboxSearch({
    activeOptionId,
    controlName,
    listboxId,
    query,
    searchInputRef,
    searchPlaceholder,
    onQueryChange,
}: {
    activeOptionId: string | undefined;
    controlName: string;
    listboxId: string;
    query: string;
    searchInputRef: React.Ref<HTMLInputElement>;
    searchPlaceholder: string;
    onQueryChange: (query: string) => void;
}) {
    return (
        <Input
            ref={searchInputRef}
            role="combobox"
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            aria-label={`Search ${controlName}`}
            aria-autocomplete="list"
            aria-expanded={true}
            aria-controls={listboxId}
            aria-activedescendant={activeOptionId}
            placeholder={searchPlaceholder}
            leftIcon="search"
            inputClassName="h-9"
        />
    );
}

function ComboboxListbox<TValue extends string>({
    activeOptionIndex,
    controlName,
    emptyText,
    filteredOptions,
    initialListScrollTop = 0,
    listClassName,
    listboxId,
    maxListHeight,
    optionClassName,
    query,
    renderOption,
    selectedValue,
    onActiveIndexChange,
    onListScrollTopChange,
    onSelect,
    preserveListScroll = false,
    skipInitialActiveScroll = false,
}: Omit<
    ComboboxPopoverProps<TValue>,
    'activeOptionId' | 'align' | 'fullWidth' | 'placementStyle' | 'popoverClassName' | 'popoverRef' | 'searchInputRef' | 'searchPlaceholder' | 'onKeyDown' | 'onQueryChange'
>) {
    const listboxRef = React.useRef<HTMLDivElement>(null);

    React.useLayoutEffect(() => {
        if (!preserveListScroll || initialListScrollTop <= 0) {
            return;
        }

        const listbox = listboxRef.current;
        if (listbox) {
            listbox.scrollTop = initialListScrollTop;
        }
    }, [initialListScrollTop, preserveListScroll]);

    return (
        <div
            ref={listboxRef}
            id={listboxId}
            role="listbox"
            aria-label={`${controlName} options`}
            className={cn('m-0 flex flex-col overflow-y-auto p-0.5', listClassName)}
            onScroll={(event) => {
                onListScrollTopChange?.(event.currentTarget.scrollTop);
            }}
            style={{ maxHeight: maxListHeight }}
        >
            {filteredOptions.map((option, index) => (
                <ComboboxOptionRow
                    key={option.value}
                    id={`${listboxId}-option-${index}`}
                    option={option}
                    index={index}
                    active={index === activeOptionIndex}
                    selected={option.value === selectedValue}
                    query={query}
                    optionClassName={optionClassName}
                    skipInitialActiveScroll={skipInitialActiveScroll}
                    renderOption={renderOption}
                    onActiveIndexChange={onActiveIndexChange}
                    onSelect={onSelect}
                />
            ))}
            {filteredOptions.length === 0 ? (
                <div className="px-3 py-4 text-center text-gray-500 text-sm dark:text-gray-400">
                    {emptyText}
                </div>
            ) : null}
        </div>
    );
}
